/**
 * Global Product Card Hydration
 *
 * Product grids render lightweight, crawlable placeholders. Full cards are fetched
 * through Shopify's Section Rendering API only when they approach the viewport.
 * Predictive-search fragments can still request immediate hydration through the
 * exported hydrateGlobalCardsInRoot function.
 */

import { ThemeEvents } from '@theme/events';

const GLOBAL_SECTION_TYPE = 'global-product-card';
const HYDRATION_BATCH_SIZE = 4;
const OBSERVER_ROOT_MARGIN = '600px 0px';

/** @type {string | null} */
let globalSectionId = null;

/** @type {Map<string, Promise<string>>} */
const fetchCache = new Map();

/** @type {IntersectionObserver | null} */
let viewportObserver = null;

/** @type {Set<Element>} */
const hydrationQueue = new Set();

/** @type {Promise<void>} */
let hydrationChain = Promise.resolve();

let filterUpdateInProgress = false;

/**
 * @returns {string}
 */
function getSectionId() {
  if (globalSectionId) return globalSectionId;

  const sectionElement = document.querySelector('[data-global-product-card][data-section-id]');
  globalSectionId = sectionElement?.getAttribute('data-section-id') || null;

  return globalSectionId || GLOBAL_SECTION_TYPE;
}

function clearGlobalCardCache() {
  fetchCache.clear();
}

/**
 * @param {ParentNode | Element} [root=document]
 */
function resetHydratedPlaceholders(root = document) {
  const placeholders = root.querySelectorAll('[data-global-card-placeholder][data-hydrated]');

  for (const placeholder of placeholders) {
    placeholder.removeAttribute('data-hydrated');
    placeholder.classList.remove('global-product-card--hydrated');
  }
}

/**
 * Returns unhydrated placeholders inside a root, including the root itself.
 *
 * @param {ParentNode | Element} root
 * @returns {Element[]}
 */
function getPlaceholders(root) {
  /** @type {Element[]} */
  const placeholders = [];

  if (
    root instanceof Element &&
    root.hasAttribute('data-global-card-placeholder') &&
    !root.hasAttribute('data-placeholder') &&
    !root.hasAttribute('data-hydrated')
  ) {
    placeholders.push(root);
  }

  placeholders.push(
    ...root.querySelectorAll(
      '[data-global-card-placeholder]:not([data-placeholder="true"]):not([data-hydrated])'
    )
  );

  return placeholders;
}

/**
 * @param {string} productUrl
 * @returns {Promise<string>}
 */
async function fetchGlobalCard(productUrl) {
  const cached = fetchCache.get(productUrl);
  if (cached) return cached;

  const request = (async () => {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', getSectionId());

    const response = await fetch(url.toString(), {
      headers: { Accept: 'text/html' },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch global card for ${productUrl}: ${response.status}`);
    }

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const output = documentFragment.querySelector('#global-product-card-output');

    if (!output) {
      throw new Error(`Missing #global-product-card-output for ${productUrl}`);
    }

    return output.innerHTML;
  })();

  fetchCache.set(productUrl, request);
  void request.catch(() => {
    if (fetchCache.get(productUrl) === request) fetchCache.delete(productUrl);
  });

  return request;
}

/**
 * @param {Element[]} placeholders
 * @returns {Promise<void>}
 */
async function hydratePlaceholders(placeholders) {
  /** @type {Map<string, Element[]>} */
  const placeholdersByUrl = new Map();

  for (const placeholder of placeholders) {
    if (placeholder.hasAttribute('data-hydrated')) continue;

    const productUrl = placeholder.getAttribute('data-product-url');
    if (!productUrl) continue;

    const targets = placeholdersByUrl.get(productUrl) || [];
    targets.push(placeholder);
    placeholdersByUrl.set(productUrl, targets);
  }

  const productUrls = [...placeholdersByUrl.keys()];

  for (let index = 0; index < productUrls.length; index += HYDRATION_BATCH_SIZE) {
    const batch = productUrls.slice(index, index + HYDRATION_BATCH_SIZE);
    const results = await Promise.allSettled(
      batch.map(async (productUrl) => {
        const cardHtml = await fetchGlobalCard(productUrl);
        const targets = placeholdersByUrl.get(productUrl) || [];

        for (const placeholder of targets) {
          if (placeholder.hasAttribute('data-hydrated')) continue;

          placeholder.innerHTML = cardHtml;
          placeholder.setAttribute('data-hydrated', 'true');
          placeholder.classList.add('global-product-card--hydrated');

          const firstSlide = placeholder.querySelector('slideshow-slide');
          if (firstSlide && firstSlide.getAttribute('aria-hidden') === 'true') {
            firstSlide.setAttribute('aria-hidden', 'false');
          }

          const lazyImages = placeholder.querySelectorAll('img[data-strict-lazy-image]');
          for (const img of lazyImages) {
            if (img.dataset.srcset) img.srcset = img.dataset.srcset;
            if (img.dataset.src) img.src = img.dataset.src;
            img.removeAttribute('data-strict-lazy-image');
            delete img.dataset.srcset;
            delete img.dataset.src;
          }
        }
      })
    );

    for (const result of results) {
      if (result.status === 'rejected') {
        console.warn('[Global Product Card]', result.reason);
      }
    }
  }
}

/**
 * Immediately hydrates placeholders in a specific root. Predictive search uses this
 * for detached result fragments that cannot be observed in the viewport.
 *
 * @param {ParentNode | Element} [root=document]
 * @returns {Promise<void>}
 */
export async function hydrateGlobalCardsInRoot(root = document) {
  await hydratePlaceholders(getPlaceholders(root));
}

/**
 * @param {Element[]} placeholders
 * @returns {Promise<void>}
 */
function enqueueHydration(placeholders) {
  for (const placeholder of placeholders) hydrationQueue.add(placeholder);

  hydrationChain = hydrationChain.then(async () => {
    const queued = [...hydrationQueue];
    hydrationQueue.clear();
    await hydratePlaceholders(queued);
  });

  return hydrationChain;
}

/**
 * @returns {IntersectionObserver | null}
 */
function getViewportObserver() {
  if (!('IntersectionObserver' in window)) return null;
  if (viewportObserver) return viewportObserver;

  viewportObserver = new IntersectionObserver(
    (entries, observer) => {
      /** @type {Element[]} */
      const entering = [];

      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        observer.unobserve(entry.target);
        if (!entry.target.hasAttribute('data-hydrated')) entering.push(entry.target);
      }

      if (entering.length > 0) void enqueueHydration(entering);
    },
    { rootMargin: OBSERVER_ROOT_MARGIN, threshold: 0.01 }
  );

  return viewportObserver;
}

/**
 * @param {ParentNode | Element} [root=document]
 */
function observeGlobalCards(root = document) {
  const placeholders = getPlaceholders(root);
  if (placeholders.length === 0) return;

  const observer = getViewportObserver();
  if (!observer) {
    void enqueueHydration(placeholders);
    return;
  }

  for (const placeholder of placeholders) observer.observe(placeholder);
}

function initialiseGlobalCards() {
  if (window.Shopify?.designMode) {
    void hydrateGlobalCardsInRoot(document);
  } else {
    observeGlobalCards(document);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialiseGlobalCards, { once: true });
} else {
  initialiseGlobalCards();
}

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;

    if (section instanceof Element && section.matches('[data-global-product-card], .global-product-card-section')) {
      globalSectionId = section.getAttribute('data-section-id') || globalSectionId;
      clearGlobalCardCache();
      resetHydratedPlaceholders(document);
    }

    void hydrateGlobalCardsInRoot(document);
  });

  document.addEventListener('shopify:section:select', (event) => {
    const section = event.target;
    if (!(section instanceof Element)) return;
    if (!section.matches('[data-global-product-card], .global-product-card-section')) return;

    clearGlobalCardCache();
    resetHydratedPlaceholders(document);
    void hydrateGlobalCardsInRoot(document);
  });
}

const mutationObserver = new MutationObserver((mutations) => {
  if (filterUpdateInProgress) return;

  for (const mutation of mutations) {
    if (mutation.type === 'childList') {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) observeGlobalCards(node);
      }
    }

    if (
      mutation.type === 'attributes' &&
      mutation.target instanceof Element &&
      mutation.target.hasAttribute('data-global-card-placeholder') &&
      !mutation.target.hasAttribute('data-placeholder') &&
      !mutation.target.hasAttribute('data-hydrated')
    ) {
      observeGlobalCards(mutation.target);
    }
  }
});

mutationObserver.observe(document.body, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['data-hydrated'],
});

document.addEventListener(ThemeEvents.FilterUpdate, () => {
  filterUpdateInProgress = true;
  clearGlobalCardCache();
  resetHydratedPlaceholders(document);

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      filterUpdateInProgress = false;
      observeGlobalCards(document);
    });
  });
});
